import Avatar from '@mui/material/Avatar';
import clsx from 'clsx';
import PersonIcon from '@mui/icons-material/Person';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import makeStyles from '@mui/styles/makeStyles';
import { useSelector } from 'react-redux';
import Button from '@katalon-studio/katalon-ui/v2/Button';
import Box from '@mui/material/Box';
import { Tooltip } from '@katalon-studio/katalon-ui/v2';
import { useConfig } from '../../../config';
import { useQuery } from '../../../routes';
import { getUserAvatar } from '../../../utils/Users';
import { fromAuth } from '../../../store';
import { getFullNameUser } from '../../../models';
import { MAX_USER_NAME_LENGTH } from '../../../utils/Constants';

const useStyles = makeStyles(theme => ({
  userBox: {
    justifyContent: 'space-between',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    padding: theme.spacing(3),
  },
  userAvatar: {
    height: 46,
    width: 46,
    margin: theme.spacing(1),
  },
}));

interface UserInfoBoxProps {
  open: boolean;
  expanded?: boolean;
  handleExpand?: () => void;
}

const CurrentUserInfo = (
  {
    open,
    expanded,
    handleExpand,
  } : UserInfoBoxProps,
) => {
  const classes = useStyles();
  const { config } = useConfig();
  const { get } = useQuery();
  const accountId = get('accountId');
  const user = useSelector(fromAuth.selectUser);
  const fullName = getFullNameUser(user?.firstName, user?.lastName, user?.email);

  const renderUserName = () => (
    <Typography
      variant="subtitle1"
      fontSize={16}
      fontWeight={700}
      color="#43509B"
      justifySelf="flex-start"
      overflow="hidden"
      textOverflow="ellipsis"
      sx={{ textWrapMode: 'nowrap' }}
    >
      <FormattedMessage
        id="layout.sidebar.user.name"
        values={{ userName: fullName }}
      />
    </Typography>
  );

  const renderUserInfo = () => (
    <Box className={classes.userBox}>
      <Box
        display="flex"
        flexDirection="row"
        width="100%"
        alignItems="center"
        alignContent="center"
        justifyContent="center"
      >
        <Box
          id="user-avatar"
          display="flex"
          width="25%"
        >
          <Avatar
            id="sidebar-user-avatar"
            src={getUserAvatar(user?.avatar, config?.publicUrl)}
            className={clsx(
              classes.userAvatar,
              { [classes.userAvatar]: open },
            )}
          >
            <PersonIcon />
          </Avatar>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          alignContent="center"
          justifyContent="center"
          width="65%"
        >
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-start"
            width="100%"
          >
            {fullName.length > MAX_USER_NAME_LENGTH
              ? (
                <Tooltip key="current-user-info-tooltip" title={fullName} placement="top-start">
                  {renderUserName()}
                </Tooltip>
              )
              : renderUserName()}
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-start"
            width="100%"
          >
            <Typography
              variant="subtitle2"
              fontSize={14}
              fontWeight={500}
            >
              <FormattedMessage
                id="layout.sidebar.user.account"
                values={{ accountId }}
              />
            </Typography>
          </Box>
        </Box>
        {open && (
          <Box
            id="expand-icon"
            display="flex"
            justifyContent="center"
            alignItems="center"
            alignContent="center"
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
        )}
      </Box>
    </Box>
  );

  return open
    ? (
      <Button
        id="sidebar-user-info"
        variant="text"
        onClick={handleExpand}
        sx={{
          width: '100%',
          height: '100%',
          padding: 0,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
      >
        {renderUserInfo()}
      </Button>
    )
    : renderUserInfo();
};

export default CurrentUserInfo;
